import React from "react";
import { Link, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  ArrowRight,
  ChevronRight,
  Briefcase,
  Sparkles,
  Layers,
} from "lucide-react";

import { ourWorkProjects } from "@/data/ourWork";
import Section from "@/components/shared/Section";
import Reveal from "@/components/shared/Reveal";
import GlowCard from "@/components/shared/GlowCard";
import Eyebrow from "@/components/shared/Eyebrow";

const OurWorkDetail = () => {
  const { slug } = useParams();
  const project = ourWorkProjects.find((item) => item.slug === slug);

  if (!project) {
    return (
      <div className="min-h-screen text-[var(--text-primary)]">
        <Section className="page-top text-center">
          <h1 className="text-3xl font-display font-bold mb-4">Project Not Found</h1>
          <p className="text-[var(--text-secondary)] mb-6 max-w-md mx-auto">
            The project you're looking for doesn't exist or has been moved.
          </p>
          <Button asChild className="btn-primary rounded-full px-6">
            <Link to="/our-work" className="inline-flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" /> Back to Our Work
            </Link>
          </Button>
        </Section>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-[var(--text-primary)]">
      {/* Hero */}
      <section className="relative page-top pb-10 overflow-hidden aurora">
        <div className="container-page relative z-10">
          <Link
            to="/our-work"
            className="inline-flex items-center gap-1.5 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> All projects
          </Link>

          <Reveal variant="up">
            <Eyebrow icon={Briefcase} className="mb-4">{project.client}</Eyebrow>
          </Reveal>
          <Reveal variant="up" delay={0.05}>
            <h1 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl mb-3 tracking-tight leading-[1.05] max-w-4xl">
              {project.title}
            </h1>
          </Reveal>
          <Reveal variant="up" delay={0.1}>
            <p className="text-xl gradient-text font-semibold">{project.type}</p>
          </Reveal>
        </div>
      </section>

      <Section size="sm">
        <Reveal variant="scale">
          <div className="rounded-3xl overflow-hidden border border-[color:var(--border-default)]">
            <img
              src={project.image}
              alt={project.title}
              className="w-full h-72 md:h-[420px] object-cover"
            />
          </div>
        </Reveal>
      </Section>

      <Section size="sm">
        <div className="grid lg:grid-cols-3 gap-7 max-w-6xl mx-auto">
          <Reveal variant="up" className="lg:col-span-2">
            <GlowCard className="p-8 sm:p-10">
              <h2 className="font-display font-semibold text-xl mb-3">Project overview</h2>
              <p className="text-[var(--text-secondary)] text-base leading-relaxed mb-7">
                {project.description}
              </p>

              <div className="mb-7">
                <h3 className="font-display font-semibold text-lg mb-3 inline-flex items-center gap-2">
                  <Layers className="w-5 h-5 text-[var(--brand-primary-light)]" /> Services Provided
                </h3>
                <div className="flex flex-wrap gap-2">
                  {project.services.map((service) => (
                    <span
                      key={service}
                      className="text-xs font-medium text-[var(--text-primary)] border border-[color:var(--border-brand)] bg-[color:var(--brand-primary)]/10 rounded-full px-3 py-1.5"
                    >
                      {service}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="font-display font-semibold text-lg mb-3">Platforms</h3>
                <div className="flex flex-wrap gap-3">
                  {project.platforms.map((platform) => (
                    <Button
                      key={platform}
                      asChild
                      variant="outline"
                      className="btn-outline rounded-full px-5 h-10"
                    >
                      <Link
                        to={`/contact?project=${encodeURIComponent(project.slug)}&platform=${platform.toLowerCase()}`}
                      >
                        {platform}
                      </Link>
                    </Button>
                  ))}
                </div>
                <p className="text-xs text-[var(--text-muted)] mt-3">
                  Want a demo or project details? Choose a platform to connect with us.
                </p>
              </div>
            </GlowCard>
          </Reveal>

          <Reveal variant="up" delay={0.1}>
            <div className="space-y-5 lg:sticky lg:top-28">
              <GlowCard className="p-7">
                <h2 className="font-display font-semibold text-lg mb-5">Quick Snapshot</h2>
                <dl className="space-y-4 text-sm">
                  <div>
                    <dt className="text-[var(--text-muted)] text-xs uppercase tracking-wider">Client</dt>
                    <dd className="font-semibold mt-0.5">{project.client}</dd>
                  </div>
                  <div>
                    <dt className="text-[var(--text-muted)] text-xs uppercase tracking-wider">Project Type</dt>
                    <dd className="font-semibold mt-0.5">{project.type}</dd>
                  </div>
                  <div>
                    <dt className="text-[var(--text-muted)] text-xs uppercase tracking-wider">Platforms</dt>
                    <dd className="font-semibold mt-0.5">{project.platforms.join(", ")}</dd>
                  </div>
                </dl>
              </GlowCard>

              <GlowCard variant="liquid" className="aurora p-7 overflow-hidden">
                <Eyebrow icon={Sparkles} className="mb-3">Get started</Eyebrow>
                <h4 className="font-display font-semibold text-lg mb-3">Want similar?</h4>
                <p className="text-[var(--text-secondary)] text-sm mb-5">
                  Tell us about your project and we'll line up the right team.
                </p>
                <div className="flex flex-col gap-2">
                  <Button asChild className="btn-primary rounded-full px-6 h-11">
                    <Link to="/contact" className="inline-flex items-center gap-2">
                      Contact us <ArrowRight className="w-4 h-4" />
                    </Link>
                  </Button>
                  <Button asChild variant="outline" className="btn-outline rounded-full px-6 h-11">
                    <Link to="/our-work" className="inline-flex items-center gap-2">
                      All projects <ChevronRight className="w-4 h-4" />
                    </Link>
                  </Button>
                </div>
              </GlowCard>
            </div>
          </Reveal>
        </div>
      </Section>
    </div>
  );
};

export default OurWorkDetail;
